import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { reportReviewService, ReviewCell, ReviewData, ReviewSolution } from '../../../services/reportReview.service';
import { formatApprovalTime } from '../../report/approvalTime';
import { Badge, Button, EmptyState, LoadingState, SearchBar, SectionHeader, Select, TableShell, Td, Th } from '../../ui';

const COLUMNS: { key: ReviewSolution; label: string }[] = [
  { key: 'NAC', label: 'NAC' },
  { key: 'DLP', label: 'DLP' },
  { key: 'WEB_FILTER', label: '유해차단' },
  { key: 'EDR', label: 'EDR' },
];

/** 계열사(2행 묶음) 첫 행만 위 경계를 굵게 그려 묶음을 구분한다 */
const groupStart = 'border-t-2 border-border';
const rowHead = 'whitespace-nowrap px-3 py-2 text-left text-xs font-semibold text-muted-foreground';

/** 작성 상태 파스텔 뱃지 — 라이트/다크 모두 대비를 따로 잡는다 (SPEC D10) */
const STATUS_BADGE = {
  written: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
  pending: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
};

const NotTarget: React.FC = () => (
  <span className="text-muted-foreground" title="이 회사는 해당 솔루션 보고서가 없습니다">
    —
  </span>
);

const WriterCell: React.FC<{ cell: ReviewCell }> = ({ cell }) => {
  if (!cell.target) return <NotTarget />;
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="inline-flex items-center gap-1.5">
        <Badge variant="outline" className={cell.written ? STATUS_BADGE.written : STATUS_BADGE.pending}>
          {cell.written ? '작성완료' : '미작성'}
        </Badge>
        {cell.writer && <span className="font-medium text-foreground">{cell.writer}</span>}
        {cell.roleConflict && (
          <Badge variant="warning" title="작성자가 승인자와 같습니다">
            역할 겹침
          </Badge>
        )}
      </span>
      {cell.writtenAt && (
        <span className="text-xs tabular-nums text-muted-foreground">{formatApprovalTime(cell.writtenAt)}</span>
      )}
    </div>
  );
};

/** 승인자 행 — 승인 후에만 승인한 사람과 시각 */
const ApproverCell: React.FC<{ cell: ReviewCell; approvedAt: string | null }> = ({ cell, approvedAt }) => {
  if (!cell.target) return <NotTarget />;
  if (!cell.approver) return null;
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-foreground">{cell.approver}</span>
      {approvedAt && <span className="text-xs tabular-nums text-muted-foreground">{formatApprovalTime(approvedAt)}</span>}
    </div>
  );
};

const countTargets = (data: ReviewData) => {
  const cells = data.rows.flatMap((r) => COLUMNS.map((c) => r.cells[c.key])).filter((c) => c.target);
  return { total: cells.length, pending: cells.filter((c) => !c.written).length };
};

/** 우측 「승인」 — 그달 대상이 전부 작성완료일 때만. Admin 전용 (SPEC §6.2) */
const ApproveControl: React.FC<{ data: ReviewData; busy: boolean; onApprove: () => void }> = ({ data, busy, onApprove }) => {
  if (data.legacy) {
    return <span className="ml-auto text-sm text-muted-foreground">202608 이전 보고서는 기존 발행 방식으로 공개되었습니다.</span>;
  }
  if (data.approvedAt) {
    return (
      <Button className="ml-auto" variant="outline" disabled>
        승인완료 {formatApprovalTime(data.approvedAt)}
      </Button>
    );
  }
  const { pending } = countTargets(data);
  return (
    <div className="ml-auto flex items-center gap-2.5">
      {pending > 0 && <span className="text-sm text-muted-foreground">미작성 {pending}건</span>}
      <Button onClick={onApprove} disabled={busy || pending > 0}>
        승인
      </Button>
    </div>
  );
};

/** 관리자 > 작성/검토이력 (IPT-627) — 월별 계열사 × 솔루션 작성 상태 + 월 승인(일괄 발행) */
const ReportReviewSection: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriod] = useState('');
  const [data, setData] = useState<ReviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reportReviewService
      .getPeriods()
      .then((list) => {
        setPeriods(list);
        setPeriod(list[0] ?? '');
      })
      .catch(() => setError('조회 가능한 월을 불러오지 못했습니다.'));
  }, []);

  const load = useCallback(async (p: string, isCancelled: () => boolean = () => false) => {
    setIsLoading(true);
    try {
      const res = await reportReviewService.getReview(p);
      if (!isCancelled()) setData(res);
    } catch {
      if (isCancelled()) return;
      setData(null);
      setError('작성/검토이력을 불러오지 못했습니다.');
    } finally {
      if (!isCancelled()) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    setError(null);
    load(period, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [period, load]);

  const approve = async () => {
    if (!data) return;
    const { total } = countTargets(data);
    if (!window.confirm(`${period} 운영보고서 ${total}건을 승인하고 일괄 발행합니다. 승인 후에는 작성완료를 취소할 수 없습니다.`)) {
      return;
    }
    setIsApproving(true);
    setError(null);
    try {
      await reportReviewService.approve(period);
      await load(period);
    } catch (e: any) {
      setError(e?.response?.data?.message || '승인에 실패했습니다.');
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <div>
      <SectionHeader
        title="작성/검토이력"
        description="작성자는 보고서를 작성완료한 사람입니다. 그달 보고서가 모두 작성완료되면 승인자(양인숙)가 승인해 일괄 발행하며, 승인자와 승인시각은 승인할 때 기록됩니다."
      />

      <SearchBar>
        <label htmlFor="report-review-period" className="text-sm font-medium text-foreground">
          조회 월
        </label>
        <Select
          id="report-review-period"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="w-auto min-w-[120px]"
        >
          {periods.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Select>
        {isAdmin && data && data.period === period && (
          <ApproveControl data={data} busy={isApproving || isLoading} onApprove={approve} />
        )}
      </SearchBar>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {isLoading && !data && <LoadingState />}

      {data && data.rows.length === 0 && <EmptyState text="해당 월의 작성/검토이력이 없습니다." />}

      {data && data.rows.length > 0 && (
        <div className={isLoading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <TableShell
            headers={
              <>
                <Th>계열사</Th>
                <Th>구분</Th>
                {COLUMNS.map((c) => (
                  <Th key={c.key} className="text-center">
                    {c.label}
                  </Th>
                ))}
              </>
            }
          >
            {data.rows.map((row, idx) => {
              const zebra = idx % 2 === 0 ? 'bg-card' : 'bg-muted/30';
              return (
                <React.Fragment key={row.companyName}>
                  <tr className={`${zebra} ${groupStart}`}>
                    <th
                      rowSpan={2}
                      scope="rowgroup"
                      className="w-[140px] border-r border-border px-3 py-2 text-center align-middle text-sm font-semibold text-foreground"
                    >
                      {row.companyName}
                    </th>
                    <th scope="row" className={rowHead}>
                      작성자
                    </th>
                    {COLUMNS.map((c) => (
                      <Td key={c.key} className="text-center">
                        <WriterCell cell={row.cells[c.key]} />
                      </Td>
                    ))}
                  </tr>
                  <tr className={zebra}>
                    <th scope="row" className={rowHead}>
                      승인자
                    </th>
                    {COLUMNS.map((c) => (
                      <Td key={c.key} className="text-center">
                        <ApproverCell cell={row.cells[c.key]} approvedAt={data.approvedAt} />
                      </Td>
                    ))}
                  </tr>
                </React.Fragment>
              );
            })}
          </TableShell>
        </div>
      )}
    </div>
  );
};

export default ReportReviewSection;
